/**
 * Global Pro Tools-inspired keyboard shortcuts overlay.
 *
 * Binds Cmd+? / Ctrl+? to toggle a help modal that lists every
 * keyboard shortcut the app supports — modeled after the Pro Tools
 * cheat sheet that every audio engineer keeps printed at their desk.
 *
 * The bindings catalogued here are the ones we actually wire up
 * (some are app-wide via this hook, others are page-specific and
 * documented here for visibility).
 *
 * Why this matters: serious engineers don't reach for the mouse.
 * The same Pro Tools muscle memory that lets a mixer cut + crossfade
 * a vocal in two seconds should work in OneVoice Studio too.
 */
import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Keyboard, X } from 'lucide-react';

/** Mac vs PC modifier glyph. */
function modKey() {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl';
}

const SECTIONS = [
  {
    title: 'Global',
    items: [
      { keys: ['MOD', '?'], desc: 'Toggle this keyboard shortcuts overlay' },
      { keys: ['MOD', 'K'], desc: 'Open the command palette (jump to any page)' },
      { keys: ['MOD', '/'], desc: 'Open the command palette (alt)' },
      { keys: ['Esc'],      desc: 'Close any open modal / overlay' },
    ],
  },
  {
    title: 'Playback (Pro Tools-compatible)',
    items: [
      { keys: ['Space'],    desc: 'Play / pause' },
      { keys: ['F'],        desc: 'Skip forward' },
      { keys: ['R'],        desc: 'Skip back (rewind)' },
      { keys: ['M'],        desc: 'Mute / unmute' },
      { keys: ['MOD', 'F'], desc: 'Add current track to favorites' },
      { keys: ['MOD', 'D'], desc: 'Download current track (when allowed)' },
    ],
  },
  {
    title: 'Studio editing (planned — Phase 3)',
    items: [
      { keys: ['MOD', 'E'], desc: 'Split clip at playhead' },
      { keys: ['MOD', 'B'], desc: 'Bounce to disk' },
      { keys: ['B'],        desc: 'Trim selection start' },
      { keys: ['N'],        desc: 'Nudge clip' },
      { keys: ['Tab'],      desc: 'Jump to next transient' },
      { keys: ['MOD', 'S'], desc: 'Save session' },
    ],
  },
  {
    title: 'Navigation',
    items: [
      { keys: ['G'],        desc: 'Go to home feed' },
      { keys: ['L'],        desc: 'Open your library' },
      { keys: ['U'],        desc: 'Upload a track' },
      { keys: ['?'],        desc: 'View this help (when no modal is open)' },
    ],
  },
];

function Combo({ keys }) {
  const mod = modKey();
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <kbd
            className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 bg-[#0f0f0f] border border-[#2a2a2a] rounded text-[#FFDA33] font-mono text-xs"
          >
            {k === 'MOD' ? mod : k}
          </kbd>
          {i < keys.length - 1 && <span className="text-[#555] text-xs">+</span>}
        </span>
      ))}
    </span>
  );
}

export function KeyboardShortcutsOverlay({ open, onOpenChange }) {
  // SSR safety — modKey() depends on navigator.
  const mod = useMemo(() => modKey(), []);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#070707] border-[#1a1a1a] text-white"
        data-testid="keyboard-shortcuts-overlay"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Chakra_Petch'] uppercase tracking-wider">
            <Keyboard size={18} className="text-[#FFDA33]" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription className="text-[#888] font-mono text-[11px]">
            Pro Tools-compatible muscle memory · Press <Combo keys={[mod, '?']} /> any time to toggle this list.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-4 max-h-[60vh] overflow-y-auto pr-2">
          {SECTIONS.map((sec) => (
            <div key={sec.title} data-testid={`shortcut-section-${sec.title.split(' ')[0].toLowerCase()}`}>
              <div className="text-[#FFDA33] font-mono text-[11px] uppercase tracking-wider mb-2 border-b border-[#1a1a1a] pb-1">
                {sec.title}
              </div>
              <ul className="space-y-2">
                {sec.items.map((it, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 text-sm"
                    data-testid={`shortcut-row-${sec.title.split(' ')[0].toLowerCase()}-${i}`}
                  >
                    <span className="text-[#ddd] font-mono text-xs flex-1">{it.desc}</span>
                    <Combo keys={it.keys} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook that binds the Cmd/Ctrl+? toggle globally.
 * Returns the controlled state so any consumer can also expose
 * a UI button to open the overlay.
 */
export function useKeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      // We need both `?` directly AND Mod+? (with Shift on most layouts
      // the slash key becomes `?`). Cover both.
      const isQuestionMark =
        e.key === '?' ||
        (e.shiftKey && e.key === '/') ||
        e.code === 'Slash';
      if ((e.metaKey || e.ctrlKey) && isQuestionMark) {
        // Don't steal focus from inputs.
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) {
          return;
        }
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}

export default KeyboardShortcutsOverlay;
